import React, {useEffect, useRef, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {SetupRequirement} from '../api/api-client';
import {useContents} from '../hooks/useContents';
import {UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Row, Col} from 'reactstrap';
import {deleteThreadWithConfirmation, renameThreadWithConfirmation} from '../utils/threadActions';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  width: number;
  onWidthChange: (width: number) => void;
  isOverlay: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ 
  isOpen, 
  onToggle, 
  width, 
  onWidthChange, 
  isOverlay 
}) => {
  const { isAuthenticated, setupNeeded } = useAuth();
  const location = useLocation();
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  // Use TanStack Query for content list
  const { data: contents = [], isLoading: loading } = useContents();


  // Handle resize functionality for desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isOverlay) return; // Don't allow resize in overlay mode
    
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(200, Math.min(600, startWidth + (e.clientX - startX)));
      onWidthChange(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Close sidebar when clicking outside in overlay mode
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOverlay && isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        onToggle();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOverlay, isOpen, onToggle]);

  if (!isAuthenticated || (setupNeeded !== SetupRequirement.None)) {
    return null;
  }
  

  const currentThreadId = location.pathname.startsWith('/threads/') ? 
    parseInt(location.pathname.split('/')[2]) : null;

  return (
    <>
      {/* Overlay backdrop for mobile */}
      {isOverlay && isOpen && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50" style={{ zIndex: 1040 }} />
      )}
      
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`
          ${isOverlay ? 'position-fixed top-0 start-0' : 'position-relative'}
          h-100 bg-body border-end d-flex flex-column
          ${isOverlay && !isOpen ? 'translate-x-n100' : ''}
        `}
        style={{
          width: `${width}px`,
          zIndex: isOverlay ? 1050 : 'auto',
          transform: isOverlay && !isOpen ? 'translateX(-100%)' : 'translateX(0)',
          transition: isResizing ? 'none' : isOverlay ? 'transform 0.3s ease-in-out' : 'none'
        }}
      >
        {/* Sidebar Header */}
        <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-bold">Threads</h6>
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={onToggle}
            title="Close sidebar"
          >
            ×
          </button>
        </div>

        {/* New Thread Button */}
        <div className="p-3 border-bottom">
          <Link to="/threads/new" className="btn btn-primary btn-sm w-100">
            + New Thread
          </Link>
        </div>

        {/* Thread List */}
        <div className="flex-grow-1 overflow-auto">
          {loading ? (
            <div className="p-3 text-center">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : contents.length === 0 ? (
            <div className="p-3 text-muted text-center small">
              No threads yet
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {contents.map((content) => (
                <Link key={content.id} to={`/threads/${content.id}`}
                  className={`
                    list-group-item list-group-item-action border-0 px-3 py-2
                    ${currentThreadId === content.id ? 'active' : ''}
                  `}
                  title={content.decryptedTitle || 'Loading...'}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="position-absolute end-0 top-0">
                        <UncontrolledDropdown><DropdownToggle tag="button"
                                  className="btn btn-sm p-1 m-1 text-muted bg-body bg-opacity-50 border border-secondary-subtle"
                                  onClick={(e) => {
                                    // THis is so we don't change selection when we click...
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                        >⋯</DropdownToggle>
                          <DropdownMenu end className="dropdown-menu-sm">
                            <DropdownItem onClick={() => renameThreadWithConfirmation(content)}>Rename...</DropdownItem>
                            <DropdownItem onClick={() => deleteThreadWithConfirmation(content)} className="text-danger">Delete...</DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                      <div className="fw-semibold small text-truncate">
                        {content.decryptedTitle || 'Loading...'}
                      </div>
                      <Row>
                        <Col className="text-muted small">
                          {new Date(content.updatedAt ?? new Date()).toLocaleDateString()}
                        </Col>
                        <Col></Col>
                      </Row>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Resize Handle (desktop only) */}
        {!isOverlay && (
          <div
            className="position-absolute top-0 end-0 h-100 cursor-resize"
            style={{ 
              width: '4px', 
              right: '-2px',
              cursor: 'col-resize',
              backgroundColor: isResizing ? 'var(--bs-primary)' : 'transparent'
            }}
            onMouseDown={handleMouseDown}
          />
        )}
      </div>
    </>
  );
};